import "./tournament-message-recipients.scss";
import { useEffect } from "react";
import { GSForm, GSItemList, GSSidePanelPage } from "golfstatus_react_components";
import { faChevronLeft, faListCheck } from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getMessage,
  createRecipientGroup,
  updateRecipientGroup,
  deleteRecipientGroup,
  selectCurrentMessage,
  selectCurrentMessageRecipientGroups,
  selectCurrentMessageExclusionGroups,
  selectMessagesLoading,
  selectMessagesNotifications,
  selectSelectedMessageTournaments,
  clearNotifications,
} from "../../../reducers/messagesSlice";
import { selectCurrentTournament } from "../../../reducers/tournamentSlice";
import { useNotificationBanner } from "../../../hooks/notificationHooks";
import TournamentMessageRecipientGroupsList from "./tournament-message-recipient-groups-list";
import { RecipientsDisclaimer } from "../../../forms/MessageForms";

const TournamentMessageRecipients = () => {
  const navigate = useNavigate();
  const match = useMatch("/:tournamentID/messages/:messageID/recipients");
  const dispatch = useDispatch();
  const currentMessage = useSelector(selectCurrentMessage);
  const currentTournament = useSelector(selectCurrentTournament);
  const recipientGroups = useSelector(selectCurrentMessageRecipientGroups);
  const exclusionGroups = useSelector(selectCurrentMessageExclusionGroups);
  const loading = useSelector(selectMessagesLoading);
  const notifications = useSelector(selectMessagesNotifications);
  const selectedTournaments = useSelector((state) =>
    selectSelectedMessageTournaments(state, match?.params?.tournamentID, currentTournament)
  );

  useEffect(() => {
    dispatch(getMessage({ tournamentID: match?.params?.tournamentID, messageID: match?.params?.messageID }));
  }, [dispatch, match?.params?.tournamentID, match?.params?.messageID]);

  const goBack = () => {
    dispatch(clearNotifications());
    navigate(-1);
  };

  const goToTournamentSelect = () => {
    dispatch(clearNotifications());
    navigate(`/${match?.params?.tournamentID}/messages/${match?.params?.messageID}/tournament-select`);
  };

  const getHeaderTitle = () => {
    if (currentMessage?.channel === "email") return "Email Recipients";
    return "App Notification Recipients";
  };

  const getFormTitle = () => {
    if (currentMessage?.channel === "email") return "Email Recipients";
    return "App Notification Recipients";
  };

  const getNavigation = () => {
    return {
      title: getHeaderTitle(),
      leftIcon: faChevronLeft,
      leftButtonClick: goBack,
    };
  };

  const getFormActions = () => {
    return [
      {
        id: "edit-tournaments",
        buttonTitle: "Manage Tournaments",
        actionIcon: faListCheck,
        type: "light-grey mobile-icon no-wrap",
        actionClick: goToTournamentSelect,
      },
    ];
  };

  const getRecipientsForTournament = (tournament) => {
    return recipientGroups?.filter?.((r) => r?.tournamentId === tournament?.id) ?? [];
  };

  const addGroup = (tournament, group) => {
    const tournamentRecipients = getRecipientsForTournament(tournament);
    const existingGroup = tournamentRecipients.find((r) => r.groupType === group.groupType);
    if (existingGroup) {
      dispatch(
        updateRecipientGroup({
          groupID: existingGroup.id,
          data: { includeAll: true },
        })
      );
    } else {
      dispatch(
        createRecipientGroup({
          tournamentID: match?.params?.tournamentID,
          messageID: match?.params?.messageID,
          data: {
            groupType: group.groupType,
            tournament: { id: tournament?.id },
            includeAll: true,
          },
        })
      );
    }
  };

  const removeGroup = (tournament, group) => {
    const tournamentRecipients = getRecipientsForTournament(tournament);
    const recipientGroup = tournamentRecipients.find((r) => r.groupType === group.groupType);
    if (!recipientGroup) return;
    dispatch(
      updateRecipientGroup({
        groupID: recipientGroup.id,
        data: { includeAll: false },
      })
    );
  };

  const getTournamentExclusion = (tournament) =>
    exclusionGroups?.find(
      (g) =>
        g?.tournamentId === tournament?.id && g?.groupType === "all_recipients"
    );

  const setTournamentAvailability = (tournament, available) => {
    const exclusion = getTournamentExclusion(tournament);
    if (available) {
      if (exclusion) {
        dispatch(deleteRecipientGroup({ groupID: exclusion.id }));
      }
    } else if (!exclusion) {
      dispatch(
        createRecipientGroup({
          tournamentID: match?.params?.tournamentID,
          messageID: match?.params?.messageID,
          data: {
            groupType: "all_recipients",
            exclusion: true,
            tournament: { id: tournament?.id },
          },
        })
      );
    }
  };

  const onManageGroup = (group) => {
    dispatch(clearNotifications());
    navigate(`${group.recipientGroupId}/manage`);
  };

  const getFormSections = () => {
    const selectedGroupCount = (recipientGroups ?? []).filter(
      (g) => g?.includeAll === true || (g?.selectedCount ?? 0) > 0
    ).length;
    return [
      {
        type: "vertical xx-large-gap no-wrap",
        fields: [
          ...(selectedGroupCount > 1
            ? [{ customView: true, value: <RecipientsDisclaimer channel={currentMessage?.channel} /> }]
            : []),
          {
            customView: true,
            value: (
              <GSItemList
                type="vertical large-gap"
                items={selectedTournaments}
                listItem={(tournament) => (
                  <TournamentMessageRecipientGroupsList
                    tournament={tournament}
                    recipients={getRecipientsForTournament(tournament)}
                    onManage={onManageGroup}
                    onAdd={(group) => addGroup(tournament, group)}
                    onRemove={(group) => removeGroup(tournament, group)}
                    isAddLoading={loading?.length > 0}
                    showAvailability={selectedTournaments.length > 1}
                    isAvailable={!getTournamentExclusion(tournament)}
                    onAvailabilityChange={(available) =>
                      setTournamentAvailability(tournament, available)
                    }
                    messageType={
                      currentMessage?.channel === "email"
                        ? "email"
                        : "app notification"
                    }
                  />
                )}
              />
            ),
          },
        ],
      },
    ];
  };

  const getContent = () => {
    return (
      <GSForm
        formTitle={getFormTitle()}
        formActions={getFormActions()}
        formSections={getFormSections()}
      />
    );
  };

  const getDrawerActions = () => {
    return [
      {
        name: "Continue",
        action: goBack,
        type: "black",
      },
    ];
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const failureNotifications = notifications?.filter((n) => n?.state === "warning");

  const [banner] = useNotificationBanner({ notifications: failureNotifications, timeoutAction });

  return (
    <tournament-message-recipients>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        content={getContent()}
        drawer={getDrawer()}
      />
    </tournament-message-recipients>
  );
};

export default TournamentMessageRecipients;
